import { useState, useEffect, useCallback } from 'react';
import { Building2, Plus } from 'lucide-react';
import axios from 'axios';
import { colors } from '../styles/colors';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useBankConnections } from '../hooks/bank/useBankConnections';
import { useQontoSync } from '../hooks/bank/useQontoSync';
import { useBankTransactions } from '../hooks/bank/useBankTransactions';
import { NETLIFY_FUNCTIONS_BASE } from '../config/api';
import PageHeader from '../components/PageHeader';
import AnimatedContent from '../components/AnimatedContent';
import QontoSetup from '../components/QontoSetup';
import ConnectionCard from '../components/banking/ConnectionCard';
import TransactionsList from '../components/banking/TransactionsList';
import DeleteConnectionModal from '../components/banking/DeleteConnectionModal';
import ManageUsersModal from '../components/banking/ManageUsersModal';

function BankingPage() {
  const { showToast } = useToast();
  const { hasPermission, ACTIONS, RESOURCES } = useAuth();
  const {
    connections,
    loading,
    error,
    refreshConnections,
    deleteConnection,
    updateAuthorizedUsers,
    updateConnectionName
  } = useBankConnections();
  const { syncQonto } = useQontoSync();
  const { transactions, fetchTransactions } = useBankTransactions();

  const [showQontoSetup, setShowQontoSetup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState(null);
  const [loadingConnectionId, setLoadingConnectionId] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [isDeletingConnection, setIsDeletingConnection] = useState(false);

  // États pour la gestion des utilisateurs autorisés
  const [users, setUsers] = useState([]);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [connectionToManage, setConnectionToManage] = useState(null);

  const canEditBanking = hasPermission(ACTIONS.WRITE, RESOURCES.BANKING);

  // Charger la liste des utilisateurs si l'utilisateur peut éditer
  const fetchUsers = useCallback(async () => {
    if (!canEditBanking) return;

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await axios.get(`${NETLIFY_FUNCTIONS_BASE}/getUsersBasic`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Erreur chargement utilisateurs:', err);
    }
  }, [canEditBanking]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  const handleCreateQontoConnection = () => {
    if (!canEditBanking) {
      showToast('Vous n\'avez pas la permission de créer des connexions bancaires', 'error');
      return;
    }
    setShowQontoSetup(true);
  };

  const handleQontoSetupSuccess = (result) => {
    showToast(result.message || 'Connexion Qonto créée avec succès', 'success');
    setShowQontoSetup(false);
    refreshConnections();
  };

  const handleRename = async (connectionId, name) => {
    try {
      await updateConnectionName(connectionId, name);
      showToast('Nom de la connexion mis à jour', 'success');
    } catch (err) {
      showToast(err.message || 'Erreur lors du renommage', 'error');
    }
  };

  const handleSyncQonto = async (connectionId) => {
    if (loadingConnectionId) return;

    setLoadingConnectionId(connectionId);
    try {
      const connection = connections.find(c => c.id === connectionId);
      console.log('[BankingPage] Démarrage sync Qonto pour:', connectionId);
      const result = await syncQonto(connectionId);
      console.log('[BankingPage] Sync terminée:', result);
      showToast(
        `Synchronisation ${connection?.bank_name || 'Qonto'} terminée: ${result.summary?.transactions_imported || 0} transactions importées`,
        'success'
      );
      refreshConnections();
      if (selectedAccountId) {
        fetchTransactions({ accountId: selectedAccountId });
      }
    } catch (err) {
      console.error('[BankingPage] Erreur sync:', err);
      showToast(err.message || 'Erreur lors de la synchronisation', 'error');
    } finally {
      setLoadingConnectionId(null);
    }
  };

  const handleDeleteConnection = async () => {
    if (!connectionToDelete) return;

    setIsDeletingConnection(true);
    try {
      const bankName = connectionToDelete.bank_name || 'la connexion';
      const result = await deleteConnection(connectionToDelete.id);
      showToast(
        result.message || `Connexion ${bankName} supprimée avec succès`,
        'success'
      );
      setShowDeleteModal(false);
      setConnectionToDelete(null);
    } catch (err) {
      showToast(err.message || 'Erreur lors de la suppression', 'error');
    } finally {
      setIsDeletingConnection(false);
    }
  };

  const openDeleteModal = (connection) => {
    if (!canEditBanking) {
      showToast('Vous n\'avez pas la permission de supprimer des connexions bancaires', 'error');
      return;
    }
    setConnectionToDelete(connection);
    setShowDeleteModal(true);
  };

  const handleAccountSelect = async (accountId) => {
    setSelectedAccountId(accountId);
    try {
      await fetchTransactions({ accountId });
    } catch (err) {
      showToast('Erreur lors du chargement des transactions', 'error');
    }
  };

  const handleCloseTransactions = () => {
    setSelectedAccountId(null);
  };

  // Gestion des utilisateurs autorisés
  const openManageUsersModal = (connection) => {
    if (!canEditBanking) {
      showToast('Vous n\'avez pas la permission de gérer les utilisateurs', 'error');
      return;
    }
    setConnectionToManage(connection);
    setShowManageUsersModal(true);
  };

  const handleSaveAuthorizedUsers = async (connectionId, userIds) => {
    try {
      await updateAuthorizedUsers(connectionId, userIds);
      showToast('Utilisateurs autorisés mis à jour', 'success');
    } catch (err) {
      showToast(err.message || 'Erreur lors de la mise à jour', 'error');
      throw err;
    }
  };

  // Récupérer les détails des utilisateurs autorisés pour une connexion
  const getAuthorizedUsersDetails = (connection) => {
    const authorizedIds = connection.authorized_users || [];
    return users.filter(u => authorizedIds.includes(u.id));
  };

  // Préparer les accounts pour tous les connexions
  const allAccounts = connections.flatMap(conn => conn.accounts || []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `4px solid ${colors.border.light}`,
            borderTopColor: colors.primary.teal,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: colors.text.secondary }}>Chargement des connexions bancaires...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Noto Sans", sans-serif'
    }}>
      <PageHeader
        icon={Building2}
        title="Connexions Bancaires"
        subtitle="Gérez vos connexions bancaires et synchronisez vos transactions"
        actions={canEditBanking && (
          <button
            onClick={handleCreateQontoConnection}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: colors.primary.teal,
              color: colors.text.white,
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: '"Noto Sans", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colors.primary.tealDark;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.primary.teal;
            }}
          >
            <Plus size={20} />
            Connecter Qonto
          </button>
        )}
      />

      <AnimatedContent>
        {/* Liste des connexions */}
        {connections.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: colors.text.secondary
          }}>
            <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
              Aucune connexion bancaire
            </p>
            <p style={{ fontSize: '0.875rem' }}>
              Commencez par connecter une banque
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '1.5rem'
          }}>
            {connections.map(connection => (
              <ConnectionCard
                key={connection.id}
                connection={connection}
                accounts={allAccounts}
                onSync={handleSyncQonto}
                onDelete={openDeleteModal}
                onManageUsers={openManageUsersModal}
                onRename={canEditBanking ? handleRename : null}
                canEdit={canEditBanking}
                isLoading={loadingConnectionId === connection.id}
                onAccountSelect={handleAccountSelect}
                selectedAccountId={selectedAccountId}
                authorizedUsers={getAuthorizedUsersDetails(connection)}
              />
            ))}
          </div>
        )}
      </AnimatedContent>

      {/* Modal de configuration Qonto */}
      <QontoSetup
        isOpen={showQontoSetup}
        onClose={() => setShowQontoSetup(false)}
        onSuccess={handleQontoSetupSuccess}
      />

      {/* Transactions list */}
      <TransactionsList
        transactions={transactions}
        selectedAccountId={selectedAccountId}
        onClose={handleCloseTransactions}
      />

      {/* Modal de confirmation de suppression */}
      <DeleteConnectionModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setConnectionToDelete(null);
        }}
        onConfirm={handleDeleteConnection}
        connection={connectionToDelete}
        isDeleting={isDeletingConnection}
      />

      {/* Modal de gestion des utilisateurs autorisés */}
      <ManageUsersModal
        show={showManageUsersModal}
        onClose={() => {
          setShowManageUsersModal(false);
          setConnectionToManage(null);
        }}
        onSave={handleSaveAuthorizedUsers}
        selectedUserIds={connectionToManage?.authorized_users || []}
        usersList={users}
        connection={connectionToManage}
      />

      {/* Animation CSS pour le spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default BankingPage;
